import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Heart, CheckCircle2, Gift, Loader2, Copy } from 'lucide-react';
import { toast } from 'sonner';

const GiftSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [giftCode, setGiftCode] = useState(null);
  const [recipientName, setRecipientName] = useState('');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) pollStatus(0);
  }, [sessionId]);

  const pollStatus = async (attempt) => {
    if (attempt >= 5) { setStatus('timeout'); return; }
    try {
      const res = await axios.get(`${API}/billing/gift/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        setGiftCode(res.data.gift_code);
        setRecipientName(res.data.recipient_name);
      } else if (res.data.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => pollStatus(attempt + 1), 2000);
      }
    } catch {
      setTimeout(() => pollStatus(attempt + 1), 2000);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(giftCode).then(() => toast.success('Code copied!')).catch(() => toast.error('Could not copy'));
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5" data-testid="gift-success">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="relative w-20 h-20 flex items-center justify-center mx-auto">
          <div className="absolute inset-0 rounded-full border-2 border-cyan-300/40" />
          <div className="absolute inset-3 rounded-full border-2 border-cyan-400/60" />
          <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
            <Heart className="w-5 h-5 text-white" fill="currentColor" />
          </div>
        </div>

        {status === 'checking' && (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-cyan-500 mx-auto" />
            <h2 className="text-xl font-bold text-foreground">Processing your gift...</h2>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
            <h2 className="text-2xl font-bold text-foreground">Gift sent!</h2>
            <p className="text-muted-foreground">
              {recipientName ? `${recipientName} will` : 'The recipient will'} receive an email with their gift code.
            </p>
            {giftCode && (
              <div className="bg-cyan-50 dark:bg-cyan-900/20 border-2 border-cyan-200 dark:border-cyan-800 rounded-xl p-5">
                <p className="text-xs text-muted-foreground mb-2">Gift code (also emailed):</p>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-2xl font-bold text-foreground tracking-widest">{giftCode}</span>
                  <button onClick={copyCode} className="p-1.5 hover:bg-muted rounded-lg transition-colors">
                    <Copy className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
              </div>
            )}
            <Button className="btn-primary w-full max-w-xs mx-auto" onClick={() => navigate('/individuals')}>
              Back to Home
            </Button>
          </>
        )}

        {status === 'timeout' && (
          <>
            <h2 className="text-xl font-bold text-foreground">Still processing</h2>
            <p className="text-muted-foreground">Your payment may still be processing. The gift email will be sent once complete.</p>
            <Button variant="outline" onClick={() => navigate('/individuals')}>Back to Home</Button>
          </>
        )}

        {status === 'expired' && (
          <>
            <h2 className="text-xl font-bold text-foreground">Session expired</h2>
            <p className="text-muted-foreground">Please try again.</p>
            <Button className="btn-primary" onClick={() => navigate('/gift')}>Try Again</Button>
          </>
        )}
      </div>
    </div>
  );
};

export default GiftSuccess;
